import { addDays, attachmentFileId, attachmentStorageKey, type AttachmentConfirmV1, type FileBucket, type IllustrationFile } from '@gk/contracts'
import { captionValue, illustrationsOf, type IllustrationLink, type KeptIllustration } from '../data/procedurePhotos.ts'
import type { BlobRow, GkDb } from '../sync/db.ts'
import type { EnqueueOptions } from '../sync/outbox.ts'
import { TransportError, type Transport } from '../sync/transport.ts'
import type { PreparedPhoto } from './capture.ts'
import { captureAllowed, PhotoQuotaError } from './store.ts'

/** The upload lane (ARCHITECTURE 9.10): after the server has REGISTERED a photo (its register command is applied: the file is pending), the device uploads the bytes under its own
 *  login, then sends `attachment.confirm`; when that is applied the server holds the photo and the device lets go of its copy. Every step is safe to repeat: the storage key carries
 *  the hash and the upload never overwrites ("already there" is success), and a repeated confirm is ignored. It runs at the end of every sync, so it works whenever the network does.
 *  A procedure version's pictures are announced by the publisher's own `document.illustrate@1` instead of a register (M16a finding 24; `illustrateFiles`, `illustrateOptions`
 *  below): the blob row names that command, and the lane treats it as it treats a register (applied: upload, then confirm).
 *  M15-V, the document lane: the same rows carry a document's bytes (a PDF, or an image for the documents bucket), announced by `attachment.register@2` or by
 *  the vault's `document.publish_version@2` in the same local transaction (`addDocument`, `documentPublishOptions` in store.ts); the lane uploads them to the
 *  bucket the server keeps them in (`bucketOf`) and confirms them with the same `attachment.confirm@1`. A document is never previewed, cached or fetched ahead
 *  (`readDocument` hands it over on demand).
 *
 *  The read side (M16-I1): a picture the person opened is kept on the device under its hash (`photoCache`, the browser's Cache storage: a content-addressed file never changes,
 *  so a copy is never stale), and the illustrations of the procedures of the jobs assigned to the person in the coming days are fetched ahead at the end of every sync, like map
 *  tiles, so the mowing pattern opens in the middle of the course with no signal. A few per run, never a whole library; a network failure ends the run and the next one tries again. */
export interface LaneDeps {
  db: GkDb
  transport: Transport
  send: (key: 'attachment.confirm@1', payload: AttachmentConfirmV1, opts?: { dependsOn?: string[]; intentKey?: string }) => Promise<{ commandId: string }>
  syncSoon(): void
  lock?: <T>(name: string, fn: () => Promise<T>) => Promise<T | undefined>
  /** The person acting on this device (the workstation's unlocked person), for the pre-fetch; none: nothing is fetched ahead. */
  person?: () => string | null
  /** The site's local day, for the window of jobs fetched ahead (`localDate(new Date(), site.timezone)`). */
  today?: () => string
  cache?: PhotoCache
  fetchBlob?: FetchBlob
}
export interface LaneSummary { uploaded: number; stored: number; failed: number; prefetched: number }

const TERMINAL = new Set(['rejected', 'conflict', 'error', 'discarded'])
const MAX_ATTEMPTS = 6
/** how far ahead the assigned jobs' illustrations are fetched (days, today included) */
export const PREFETCH_DAYS = 7
/** how many pictures one run fetches ahead at most (a field network; the next sync continues) */
export const PREFETCH_PER_RUN = 12

function defaultLock(name: string) {
  const busy = new Set<string>()
  return async <T,>(_n: string, fn: () => Promise<T>): Promise<T | undefined> => {
    const locks = (globalThis as { navigator?: Navigator }).navigator?.locks
    if (locks) return locks.request(name, { ifAvailable: true }, async (l) => (l ? fn() : undefined)) as Promise<T | undefined>
    if (busy.has(name)) return undefined
    busy.add(name)
    try { return await fn() } finally { busy.delete(name) }
  }
}

// ---------------------------------------------------------------------------------------------------------- the read-side cache
export interface PhotoCache { has(sha256: string): Promise<boolean>; get(sha256: string): Promise<Blob | null>; put(sha256: string, blob: Blob): Promise<void> }
export type FetchBlob = (url: string) => Promise<Blob>
const CACHE_NAME = 'gk-photos-v1'
const cacheKey = (sha: string) => `/__photo/${sha}`
let shared: PhotoCache | null = null
/** The device's copy of the pictures it read, under their hash: the browser's Cache storage when there is one, else a cache that holds nothing (a private window, a test). */
export function photoCache(): PhotoCache {
  if (shared) return shared
  const open = async (): Promise<Cache | null> => (typeof caches === 'undefined' ? null : caches.open(CACHE_NAME).catch(() => null))
  shared = {
    async has(sha) { const c = await open(); return !!(await c?.match(cacheKey(sha)).catch(() => undefined)) },
    async get(sha) { const c = await open(); const hit = await c?.match(cacheKey(sha)).catch(() => undefined); return hit ? hit.blob() : null },
    async put(sha, blob) { const c = await open(); await c?.put(cacheKey(sha), new Response(blob, { headers: { 'content-type': blob.type || 'image/jpeg' } })).catch(() => undefined) },
  }
  return shared
}
/** An in-memory cache for tests, or a device with no Cache storage that still wants a session's worth of memory. */
export function memoryPhotoCache(): PhotoCache & { keys(): string[] } {
  const m = new Map<string, Blob>()
  return { async has(s) { return m.has(s) }, async get(s) { return m.get(s) ?? null }, async put(s, b) { m.set(s, b) }, keys: () => [...m.keys()] }
}
const defaultFetchBlob: FetchBlob = async (url) => {
  const r = await fetch(url)
  if (!r.ok) throw new TransportError('server', `photo ${r.status}`)
  return r.blob()
}
const storageKeyOf = (link: IllustrationLink, orgId: string) => attachmentStorageKey(link.org_id ?? orgId, link.site_id, link.sha256, link.mime)
/** M15-V: the bucket a link names (the server copies it from the file); a row pulled before the column existed is a photo of the attachments bucket */
export const linkBucket = (link: object): FileBucket => ((link as { bucket?: unknown }).bucket === 'documents' ? 'documents' : 'attachments')

const reading = new Map<string, Promise<Blob | null>>()          // one read per picture at a time in a session, shared by every element that shows it
/** The bytes of a linked picture: this device's own copy while it still holds one (and kept from then on), else the cache, else storage (and then the cache: a picture the
 *  person opened is there the next time, with no signal). null when it is neither on the device nor reachable now. */
export function readPhoto(d: { cache?: PhotoCache; transport: Pick<Transport, 'attachmentUrl'>; fetchBlob?: FetchBlob }, link: IllustrationLink, orgId: string, local?: Blob): Promise<Blob | null> {
  const cache = d.cache ?? photoCache()
  if (local) { void cache.has(link.sha256).then((h) => (h ? undefined : cache.put(link.sha256, local))); return Promise.resolve(local) }
  const have = reading.get(link.sha256)
  if (have) return have
  const p = (async () => {
    const hit = await cache.get(link.sha256)
    if (hit) return hit
    const blob = await (d.fetchBlob ?? defaultFetchBlob)(await d.transport.attachmentUrl(storageKeyOf(link, orgId), linkBucket(link)))
    await cache.put(link.sha256, blob)
    return blob
  })().catch(() => null).finally(() => { reading.delete(link.sha256) })
  reading.set(link.sha256, p)
  return p
}

/** M15-V: the bytes of a document to hand over (a download): this device's own copy while it still holds one (the lane has not sent it yet), else storage through
 *  a signed URL. Never kept in the photo cache and never shown: a PDF is named and sized on the device and opened by the phone's own viewer. Throws when
 *  neither is at hand (no signal, or not uploaded yet). */
export async function readDocument(d: { transport: Pick<Transport, 'attachmentUrl'>; fetchBlob?: FetchBlob }, link: IllustrationLink & { bucket?: unknown }, orgId: string, local?: Blob): Promise<Blob> {
  if (local) return local
  return (d.fetchBlob ?? defaultFetchBlob)(await d.transport.attachmentUrl(storageKeyOf(link, orgId), linkBucket(link)))
}

// ---------------------------------------------------------------------------------------------------------- what to fetch ahead
export interface PrefetchRows {
  assignments: ReadonlyArray<{ job_id: string; person_id: string; removed_by_command_id: string | null }>
  jobs: ReadonlyArray<{ id: string; template_version_id: string | null; scheduled_local_date: string; state: string }>
  templateVersions: ReadonlyArray<{ id: string; procedure_document_version_id: string | null }>
  sessions: ReadonlyArray<{ person_id: string; closed_at: string | null; job_id: string | null; template_version_id: string | null; procedure_version_id: string | null }>
  links: ReadonlyArray<IllustrationLink>
}
/** The illustrations the person will want with no signal: those of the procedures of the jobs assigned to them from today over `days` days (not cancelled), and of their open
 *  timers; each picture once (by hash), the nearest job first. Pure. */
export function prefetchTargets(rows: PrefetchRows, personId: string, today: string, days = PREFETCH_DAYS): IllustrationLink[] {
  const last = addDays(today, days - 1)
  const tvOf = new Map(rows.templateVersions.map((v) => [v.id, v.procedure_document_version_id]))
  const jobOf = new Map(rows.jobs.map((j) => [j.id, j]))
  const versions: Array<{ date: string; id: string }> = []
  for (const a of rows.assignments) {
    if (a.person_id !== personId || a.removed_by_command_id) continue
    const j = jobOf.get(a.job_id)
    if (!j || j.state === 'cancelled' || j.scheduled_local_date < today || j.scheduled_local_date > last) continue
    const v = j.template_version_id ? tvOf.get(j.template_version_id) : null
    if (v) versions.push({ date: j.scheduled_local_date, id: v })
  }
  for (const s of rows.sessions) {
    if (s.person_id !== personId || s.closed_at) continue
    const v = s.procedure_version_id ?? (s.template_version_id ? tvOf.get(s.template_version_id) : null) ?? (s.job_id && jobOf.get(s.job_id)?.template_version_id ? tvOf.get(jobOf.get(s.job_id)!.template_version_id!) : null)
    if (v) versions.push({ date: '', id: v })                    // a running job comes first
  }
  versions.sort((a, b) => a.date.localeCompare(b.date))
  const seenVersion = new Set<string>(), seenSha = new Set<string>()
  const out: IllustrationLink[] = []
  for (const v of versions) {
    if (seenVersion.has(v.id)) continue
    seenVersion.add(v.id)
    for (const l of illustrationsOf(rows.links, v.id)) if (!seenSha.has(l.sha256)) { seenSha.add(l.sha256); out.push(l) }
  }
  return out
}

// ---------------------------------------------------------------------------------------------------------- the publisher's pictures (M16a finding 24)
/** The files of `document.illustrate@1`: the pictures kept from the version in force (their link's fingerprint, size, type and instant: the server finds the file it already
 *  holds and links it again) and the new ones prepared on this device (the server creates the file pending; the lane uploads the bytes), each with its caption trimmed, or
 *  none. Pure: the order is the form's (kept first, then new). */
export function illustrateFiles(kept: readonly KeptIllustration[], fresh: ReadonlyArray<{ photo: PreparedPhoto; caption: string }>, now: () => string = () => new Date().toISOString()): IllustrationFile[] {
  const files: IllustrationFile[] = []
  for (const k of kept) {
    if (!k.keep) continue
    const caption = captionValue(k.caption)
    files.push({ sha256: k.link.sha256, bytes: k.link.bytes, mime: k.link.mime as IllustrationFile['mime'], captured_at: k.link.captured_at ?? now(), ...(caption ? { caption } : {}) })
  }
  for (const { photo, caption } of fresh) {
    const c = captionValue(caption)
    files.push({ sha256: photo.sha256, bytes: photo.bytes, mime: photo.mime, captured_at: photo.capturedAt, ...(c ? { caption: c } : {}) })
  }
  return files
}
/** The bytes of the NEW pictures are kept on the device in the SAME local transaction as the `document.illustrate@1` command that announces them (`sendAndWait(..., { alsoWrite })`,
 *  as `addPhoto` does with a register): the lane uploads each once the command is applied and confirms it; a picture this device already holds (taken for another record, or
 *  a retry) only gains the command. Refuses before anything is written when the device is short of storage (M8). */
export async function illustrateOptions(db: GkDb, orgId: string, siteId: string, photos: readonly PreparedPhoto[]): Promise<EnqueueOptions> {
  if (photos.length > 0 && !(await captureAllowed())) throw new PhotoQuotaError()
  return {
    alsoWrite: async (row) => {
      for (const photo of photos) {
        const existing = await db.blobs.get(photo.sha256)
        if (existing) {
          await db.blobs.update(photo.sha256, { register_command_ids: [...existing.register_command_ids, row.command_id], ...(existing.state === 'failed' ? { state: 'registered' as const, attempts: 0 } : {}), sensitive: undefined })
          continue
        }
        const blob: BlobRow = {
          sha256: photo.sha256, bytes: photo.bytes, mime: photo.mime, blob: photo.blob, file_id: attachmentFileId(orgId, photo.sha256), storage_key: attachmentStorageKey(orgId, siteId, photo.sha256, photo.mime),
          register_command_ids: [row.command_id], state: 'registered', attempts: 0, created_at: new Date().toISOString(),
        }
        await db.blobs.add(blob)
      }
    },
  }
}

export class PhotoLane {
  private readonly lock: NonNullable<LaneDeps['lock']>
  constructor(private readonly d: LaneDeps) { this.lock = d.lock ?? defaultLock(`gk-photos:${d.db.name}`) }

  async run(): Promise<LaneSummary> {
    const sum: LaneSummary = { uploaded: 0, stored: 0, failed: 0, prefetched: 0 }
    await this.lock('photos', async () => {
      for (const row of await this.d.db.blobs.toArray()) {
        try { await this.step(row, sum) } catch (e) {
          if (e instanceof TransportError && (e.kind === 'network' || e.kind === 'auth' || e.kind === 'frozen')) return      // not now: the rest, the pre-fetch included, would fail the same way
          const attempts = row.attempts + 1
          if (attempts >= MAX_ATTEMPTS) { await this.giveUp(row, sum, e instanceof TransportError ? (e.code || e.message) : String(e)); continue }
          await this.d.db.blobs.update(row.sha256, { attempts, last_error: e instanceof TransportError ? (e.code || e.message) : String(e) })
        }
      }
      await this.prefetch(sum)
    })
    return sum
  }

  /** M15-V: where the bytes go. The server's word first — a link of this file in the mirror names its bucket (the club may hold these bytes already, where
   *  they first landed: an image is admitted by both buckets, and a file is one per club and fingerprint) — else what the device asked for (the blob row:
   *  a PDF or a vault file, 'documents'), else the photos' bucket. A link of a sensitive record never reaches the device: the row's word then. */
  private async bucketOf(row: BlobRow): Promise<FileBucket> {
    const link = await this.d.db.attachment_link.filter((l) => l.file_id === row.file_id && (l.bucket === 'attachments' || l.bucket === 'documents')).first()
    return (link?.bucket as FileBucket | undefined) ?? row.bucket ?? 'attachments'
  }

  /** Whether storage already holds the object and this login may read it (a signed URL is given for it). */
  private async held(key: string, bucket: FileBucket): Promise<boolean> {
    try { await this.d.transport.attachmentUrl(key, bucket); return true } catch { return false }
  }

  /** The bytes cannot reach the server (refused, or the upload failed for good): kept as 'failed' for the sync page (Réessayer / Ignorer), unless they are a
   *  sensitive record's (release b review, finding 31: an incident's evidence or a cost's invoice is never kept on the device once its fate is known). */
  private async giveUp(row: BlobRow, sum: LaneSummary, code: string): Promise<void> {
    if (row.sensitive) await this.d.db.blobs.delete(row.sha256)
    else await this.d.db.blobs.update(row.sha256, { state: 'failed', attempts: MAX_ATTEMPTS, last_error: code })
    sum.failed++
  }

  private async appliedRegister(row: BlobRow): Promise<string | null> {
    for (const id of row.register_command_ids) if (await this.d.db.archive.get(id)) return id
    return null
  }

  private async step(row: BlobRow, sum: LaneSummary): Promise<void> {
    const { db, transport } = this.d
    if (row.state === 'failed') { if (row.sensitive) await db.blobs.delete(row.sha256); return }     // one an older build kept
    if (row.state === 'registered') {
      const registered = await this.appliedRegister(row)
      if (!registered) {
        const outs = await Promise.all(row.register_command_ids.map((id) => db.outbox.get(id)))
        if (outs.length > 0 && outs.every((o) => o && TERMINAL.has(o.status))) {            // the server refused every registration: the picture belongs to nothing
          await this.giveUp(row, sum, outs[0]?.server_code ?? 'REFUSED')
        }
        return
      }
      const bucket = await this.bucketOf(row)
      try {
        await transport.uploadAttachment(row.storage_key, row.blob, row.mime, bucket)                 // 'ok' and 'exists' are both fine
      } catch (e) {
        // M15-V: the server refuses the bytes of a key it does not wait for (no PENDING file under it). The club may hold these bytes already — the same PDF
        // published as a second version, the same sheet for two products, a photo sent twice — and then they are in storage: when this login may read them
        // there is nothing left to upload, and the confirm is answered at once (a stored file: a silent repeat). Anything else is the old refusal.
        if (!(e instanceof TransportError) || e.kind === 'network' || e.kind === 'auth' || e.kind === 'frozen' || !(await this.held(row.storage_key, bucket))) throw e
      }
      const { commandId } = await this.d.send('attachment.confirm@1', { file_id: row.file_id }, { dependsOn: [registered], intentKey: `confirm|${row.file_id}` })
      await db.blobs.update(row.sha256, { state: 'uploaded', confirm_command_id: commandId, last_error: undefined })
      sum.uploaded++
      this.d.syncSoon()
      return
    }
    // uploaded: wait for the server to confirm, then let go of the bytes
    if (row.confirm_command_id && await db.archive.get(row.confirm_command_id)) { await db.blobs.delete(row.sha256); sum.stored++; return }
    const out = row.confirm_command_id ? await db.outbox.get(row.confirm_command_id) : undefined
    if (out && TERMINAL.has(out.status)) { await this.giveUp(row, sum, out.server_code ?? 'REFUSED'); return }
    if (!out) {                                                                              // the confirm left the outbox without being applied (cleared): send it again
      const registered = await this.appliedRegister(row)
      const { commandId } = await this.d.send('attachment.confirm@1', { file_id: row.file_id }, { ...(registered ? { dependsOn: [registered] } : {}), intentKey: `confirm|${row.file_id}|${Date.now()}` })
      await db.blobs.update(row.sha256, { confirm_command_id: commandId })
    }
  }

  /** The pictures the person will want with no signal (`prefetchTargets`), from the device's mirror; a few per run, the ones already held skipped. A picture this device
   *  took itself is not fetched: it is in `blobs` until stored and the read side keeps it. */
  private async prefetch(sum: LaneSummary): Promise<void> {
    const person = this.d.person?.() ?? null, today = this.d.today?.()
    if (!person || !today) return
    const { db } = this.d
    const cache = this.d.cache ?? photoCache()
    const assignments = (await db.assignment.where('person_id').equals(person).toArray()) as unknown as PrefetchRows['assignments']
    const sessions = ((await db.work_session.toArray()) as unknown as PrefetchRows['sessions']).filter((s) => s.person_id === person && !s.closed_at)
    if (assignments.length === 0 && sessions.length === 0) return
    const jobIds = new Set([...assignments.map((a) => a.job_id), ...sessions.flatMap((s) => (s.job_id ? [s.job_id] : []))])
    const jobs = ((await db.job.bulkGet([...jobIds])).filter((j): j is NonNullable<typeof j> => !!j)) as unknown as PrefetchRows['jobs']
    const templateVersions = (await db.job_template_version.toArray()) as unknown as PrefetchRows['templateVersions']
    const links = (await db.attachment_link.toArray()) as unknown as PrefetchRows['links']
    let budget = PREFETCH_PER_RUN
    const held = new Set((await db.blobs.toArray()).map((b) => b.sha256))
    for (const link of prefetchTargets({ assignments, jobs, templateVersions, sessions, links }, person, today)) {
      if (budget <= 0) return
      if (!link.org_id || held.has(link.sha256) || await cache.has(link.sha256)) continue
      budget--
      try {
        const blob = await (this.d.fetchBlob ?? defaultFetchBlob)(await this.d.transport.attachmentUrl(storageKeyOf(link, link.org_id), linkBucket(link)))
        await cache.put(link.sha256, blob)
        sum.prefetched++
      } catch (e) {
        if (e instanceof TransportError && (e.kind === 'network' || e.kind === 'auth' || e.kind === 'frozen')) return    // not now
        if (e instanceof TypeError) return                                                                             // fetch itself failed: no network
        // anything else (not uploaded yet, no right to read it): the next picture may still come
      }
    }
  }
}
